import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Utensils } from "lucide-react";
import { Panel, Btn, Field, Input, Select } from "@/components/kit";
import api, { formatApiError } from "@/lib/api";

export function ServicesManager() {
  const [data, setData] = useState({ locations: [], departments: [], services: [] });
  const [form, setForm] = useState({ name: "", location_id: "", department_id: "", description: "", capacity: 20, duration_min: 60, food_related: false });

  const load = () => Promise.all([api.get("/locations"), api.get("/departments"), api.get("/services")])
    .then(([l, d, s]) => setData({ locations: l.data, departments: d.data, services: s.data })).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try {
      await api.post("/services", { ...form, capacity: Number(form.capacity), duration_min: Number(form.duration_min) });
      toast.success("Service added");
      setForm({ ...form, name: "", description: "" });
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };
  const deptsForLoc = data.departments.filter((d) => d.location_id === form.location_id);

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <Panel className="p-6">
        <h3 className="font-head font-bold text-white mb-4 flex items-center gap-2"><Utensils size={18} /> Add a service</h3>
        <form onSubmit={add} className="space-y-3" data-testid="svcmgr-form">
          <Field label="Location"><Select data-testid="svcmgr-location" required value={form.location_id} onChange={(e) => setForm({ ...form, location_id: e.target.value, department_id: "" })}><option value="">Select…</option>{data.locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}</Select></Field>
          <Field label="Department"><Select data-testid="svcmgr-dept" required value={form.department_id} onChange={(e) => setForm({ ...form, department_id: e.target.value })}><option value="">Select…</option>{deptsForLoc.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}</Select></Field>
          <Field label="Name"><Input data-testid="svcmgr-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Description"><Input data-testid="svcmgr-desc" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Capacity"><Input data-testid="svcmgr-cap" type="number" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} /></Field>
            <Field label="Duration (min)"><Input data-testid="svcmgr-dur" type="number" value={form.duration_min} onChange={(e) => setForm({ ...form, duration_min: e.target.value })} /></Field>
          </div>
          <label className="flex items-center gap-2 text-sm text-purple-200"><input data-testid="svcmgr-food" type="checkbox" checked={form.food_related} onChange={(e) => setForm({ ...form, food_related: e.target.checked })} /> Food related (requires allergy handling)</label>
          <Btn type="submit" data-testid="svcmgr-add">Add service</Btn>
        </form>
      </Panel>
      <div className="space-y-3">
        <h3 className="font-head font-bold text-white">Services <span className="text-sm text-purple-400 font-normal">({data.services.length})</span></h3>
        {!data.services.length ? <Panel className="p-8 text-center text-purple-400/70">No services yet.</Panel> : data.services.map((s) => (
          <Panel key={s.id} className="p-4" data-testid={`svcmgr-item-${s.id}`}>
            <div className="flex justify-between"><h4 className="font-bold text-white">{s.name}</h4>{s.food_related && <span className="text-xs text-rose-300">food</span>}</div>
            <p className="text-xs text-purple-400">{s.department_name} · cap {s.capacity} · {s.duration_min}min</p>
            {s.description && <p className="text-sm text-purple-200/70 mt-1">{s.description}</p>}
          </Panel>
        ))}
      </div>
    </div>
  );
}
